import React, { useState } from 'react';
import { Plus, Trash2, Edit2, Check, X, Lock } from 'lucide-react';

const emptyForm = {
  nombre: '',
  descripcion: '',
  afecta_ss: false,
  afecta_isr: true,
  activo: true
};

export default function TiposIngresosTab({ incomeTypes, createIncomeType, updateIncomeType, deleteIncomeType }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError('');
    setShowForm(true);
  };

  const handleOpenEdit = (t) => {
    setForm({
      nombre: t.nombre,
      descripcion: t.descripcion || '',
      afecta_ss: t.afecta_ss,
      afecta_isr: t.afecta_isr,
      activo: t.activo
    });
    setEditingId(t.id);
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await updateIncomeType(editingId, form);
      } else {
        await createIncomeType(form);
      }
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este tipo de ingreso?')) return;
    try {
      await deleteIncomeType(id);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={handleOpenCreate}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-sm"
        >
          <Plus className="w-4 h-4" />
          Nuevo Tipo de Ingreso
        </button>
      </div>

      {showForm && (
        <div className="border border-green-200 bg-green-50 rounded-xl p-5 space-y-3">
          <h4 className="font-semibold text-green-800">{editingId ? 'Editar Tipo de Ingreso' : 'Nuevo Tipo de Ingreso'}</h4>
          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre *</label>
              <input
                value={form.nombre}
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="Ej: Comisiones"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Descripción</label>
              <input
                value={form.descripcion}
                onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="Descripción opcional"
              />
            </div>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.afecta_ss} onChange={e => setForm(f => ({ ...f, afecta_ss: e.target.checked }))} className="w-4 h-4 accent-green-600" />
              Afecta Seguridad Social
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.afecta_isr} onChange={e => setForm(f => ({ ...f, afecta_isr: e.target.checked }))} className="w-4 h-4 accent-green-600" />
              Afecta ISR
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.activo} onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))} className="w-4 h-4 accent-green-600" />
              Activo
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100">
              <X className="w-4 h-4 inline mr-1" />Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
              <Check className="w-4 h-4 inline mr-1" />{saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden border border-gray-200 rounded-xl">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Nombre</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Descripción</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">SS</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">ISR</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">Estado</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {incomeTypes.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">No hay tipos de ingresos configurados</td></tr>
            ) : (
              incomeTypes.map(t => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">
                    {t.nombre}
                    {t.es_sistema && <Lock className="w-3 h-3 inline ml-1.5 text-gray-400" title="Sistema" />}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{t.descripcion || '—'}</td>
                  <td className="px-4 py-3 text-center">{t.afecta_ss ? <Check className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-center">{t.afecta_isr ? <Check className="w-4 h-4 text-green-600 mx-auto" /> : <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${t.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {t.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleOpenEdit(t)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Editar">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      {!t.es_sistema && (
                        <button onClick={() => handleDelete(t.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Eliminar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
